import { Container, InputField, PasswordField, SubmitButton } from '@/components/Auth/Buttons';
import { PasswordRequirements } from '@/components/Auth/PasswordRequirements';
import { useAuth } from '@/providers/AuthProvider';
import { SignUpInput } from 'aws-amplify/auth';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckboxField } from '@/components/Auth/CheckboxField';
import { isPasswordValid } from './SignUp';
import config from '@/config';
import { CheckIcon } from '@/components/Icons/CommonIcons';

// Define types for state
type SignUpInputState = SignUpInput & {
  options: {
    userAttributes: {
      family_name: string;
      given_name: string;
      'custom:organization': string;
    };
  };
};

type ConfirmSignUpInputState = {
  username: string;
  confirmationCode: string;
};

/**
 * SignUp component for user registration
 */
export default function FreeTrialSignUp() {
  const [loading, setLoading] = useState(false);
  const [signUpInput, setSignUpInput] = useState<SignUpInputState>({
    username: '',
    password: '',
    options: {
      userAttributes: {
        family_name: '',
        given_name: '',
        'custom:organization': ''
      }
    }
  });
  const [confirmSignUpInput, setConfirmSignUpInput] = useState<ConfirmSignUpInputState>({
    username: '',
    confirmationCode: ''
  });
  const [confirmSignUp, setConfirmSignUp] = useState(false);
  const [banner, setBanner] = useState<string | undefined>();
  const [bannerVariant, setBannerVariant] = useState<'error' | 'success'>('error');
  const [showPassword, setShowPassword] = useState(false);
  const [acknowledgedConfirmation, setAcknowledgedConfirmation] = useState(false);

  const auth = useAuth();
  const navigate = useNavigate();

  const showError = (message?: string) => {
    setBanner(message);
    setBannerVariant('error');
  };

  const showSuccess = (message: string) => {
    setBanner(message);
    setBannerVariant('success');
  };

  const cleanErrorMessage = (rawMessage: string) => {
    return rawMessage
      ?.replace(/^[\w\s]+failed with error[:\s]*/i, '')
      ?.replace(/^[A-Za-z]+Exception[:\s]*/i, '')
      ?.trim();
  };

  const extractErrorCode = (message: string) => {
    if (!message || typeof message !== 'string') return null;
    const match = message.match(/ERROR_\w+/);
    return match ? match[0] : null;
  };

  const setCleanedError = (rawMessage: string) => {
    const cleaned = cleanErrorMessage(rawMessage) || rawMessage;
    const code = extractErrorCode(cleaned);
    showError(code ?? cleaned);
  };

  useEffect(() => {
    const currentUrl = window.location.hostname;

    if (currentUrl !== 'localhost' && currentUrl !== config.siteDomain) {
      const newUrl = `${window.location.protocol}//${config.siteDomain}${window.location.pathname}${window.location.search}${window.location.hash}`;
      window.location.href = newUrl;
    }
  }, []);

  /**
   * Handles the sign up process
   */
  const signUp = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);

    try {
      const normalizedUsername = signUpInput.username.trim().toLowerCase();
      const trimmedPassword = (signUpInput.password ?? '').trim();

      if (!isFormValid()) {
        showError('Please fill out all fields');
        return;
      }

      const passwordValidation = isPasswordValid(trimmedPassword);
      if (!passwordValidation.valid) {
        showError(passwordValidation.message);
        setLoading(false);
        return;
      }

      const result = await auth.signUp({
        ...signUpInput,
        username: normalizedUsername,
        password: trimmedPassword,
        options: {
          userAttributes: {
            ...signUpInput.options.userAttributes,
            email: normalizedUsername
          }
        }
      });
      if (result?.nextStep.signUpStep === 'CONFIRM_SIGN_UP') {
        setConfirmSignUp(true);
        setConfirmSignUpInput({ username: normalizedUsername, confirmationCode: '' });
      } else {
        await auth.signIn({
          username: normalizedUsername,
          password: trimmedPassword
        });
        navigate('/');
      }
    } catch (err: any) {
      setCleanedError(err?.message || 'Unable to sign up');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handles the confirmation of sign up
   */
  const handleConfirm = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);

    try {
      const normalizedUsername = signUpInput.username.trim().toLowerCase();
      const trimmedPassword = (signUpInput.password ?? '').trim();
      const trimmedConfirmationCode = confirmSignUpInput.confirmationCode.trim();

      await auth.confirmSignUp({
        username: normalizedUsername,
        confirmationCode: trimmedConfirmationCode
      });

      await auth.signIn({
        username: normalizedUsername,
        password: trimmedPassword
      });
      navigate('/dashboard/getting-started');
    } catch (err: any) {
      setCleanedError(err?.message || 'Unable to confirm sign up');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async (event: React.FormEvent) => {
    event.preventDefault();

    try {
      const normalizedUsername = signUpInput.username.trim().toLowerCase();

      await auth.resendSignUpCode({
        username: normalizedUsername
      });
      showSuccess('Confirmation code resent!');
    } catch (err: any) {
      setCleanedError(err?.message || 'Unable to resend code');
    }
  };

  /**
   * Toggles password visibility
   */
  const togglePasswordVisibility = () => setShowPassword(!showPassword);

  /**
   * Validates if all required fields are filled
   */
  const isFormValid = () => {
    return (
      signUpInput.username.trim() !== '' &&
      (signUpInput.password ?? '').trim() !== '' &&
      signUpInput.options.userAttributes.given_name !== '' &&
      signUpInput.options.userAttributes.family_name !== '' &&
      signUpInput.options.userAttributes['custom:organization'] !== '' &&
      acknowledgedConfirmation
    );
  };

  /**
   * Updates the sign up input state
   */
  const updateSignUpInput = (field: string, value: string) => {
    showError();
    setSignUpInput((prevInput) => ({
      ...prevInput,
      [field]: value
    }));
  };

  /**
   * Updates the user attributes in sign up input state
   */
  const updateUserAttribute = (field: string, value: string) => {
    showError();
    setSignUpInput((prevInput) => ({
      ...prevInput,
      options: {
        userAttributes: {
          ...prevInput.options.userAttributes,
          [field]: value
        }
      }
    }));
  };

  return (
    <Container
      title="Create Your Scribl Account"
      subtitle={
        <div className="flex items-center justify-center gap-8">
          <div className="flex items-center justify-center gap-4">
            <CheckIcon /> 21 day free trial!
          </div>
          |
          <div className="flex items-center justify-center gap-4">
            <CheckIcon /> Invite colleagues!
          </div>
        </div>
      }
      banner={banner}
      bannerVariant={bannerVariant}
      className="relative md:w-600 px-20 py-32 md:px-100 md:py-32 rounded-[12px] bg-white backdrop-blur-xs shadow-xs z-1"
    >
      <form className="w-full flex flex-col space-y-12">
        {confirmSignUp ? (
          <>
            <p className="mt-3 mb-10">
              Please check your email for the confirmation code.
              <p className="mt-3">
                Did not receive the code?{' '}
                <button type="button" className="link link-sm font-semibold" onClick={handleResend}>
                  Resend
                </button>
              </p>
            </p>
            <InputField
              aria-label="Confirmation Code"
              name="confirmationCode"
              label="Confirmation Code"
              value={confirmSignUpInput.confirmationCode}
              onChange={(e) =>
                setConfirmSignUpInput({ ...confirmSignUpInput, confirmationCode: e.target.value })
              }
            />
          </>
        ) : (
          <div className="space-y-12">
            <InputField
              name="firstName"
              label="First Name"
              value={signUpInput.options.userAttributes.given_name}
              onChange={(e) => updateUserAttribute('given_name', e.target.value)}
            />
            <InputField
              name="lastName"
              label="Last Name"
              value={signUpInput.options.userAttributes.family_name}
              onChange={(e) => updateUserAttribute('family_name', e.target.value)}
            />
            <InputField
              name="organization"
              label="Your Organization"
              value={signUpInput.options.userAttributes['custom:organization']}
              onChange={(e) => updateUserAttribute('custom:organization', e.target.value)}
              error={banner === 'ERROR_AUTH_COMPANY_UNAVAILABLE'}
            />
            <InputField
              name="email"
              label="Email Address"
              value={signUpInput.username}
              onChange={(e) => updateSignUpInput('username', e.target.value)}
              error={banner === 'ERROR_AUTH_EXISTING_USER'}
            />
            <div className="group">
              <PasswordField
                value={signUpInput.password ?? ''}
                onChange={(e) => updateSignUpInput('password', e.target.value)}
                showPassword={showPassword}
                togglePasswordVisibility={togglePasswordVisibility}
              />
              <div className="hidden group-focus-within:block">
                <PasswordRequirements password={signUpInput.password ?? ''} />
              </div>
            </div>
          </div>
        )}
        <SubmitButton
          title={confirmSignUp ? 'Confirm' : 'Create Account'}
          loading={loading}
          onSubmit={confirmSignUp ? handleConfirm : signUp}
        />
        {!confirmSignUp && (
          <CheckboxField
            label="I have read and agree to the"
            link={{
              text: 'End User License Agreement',
              url: 'https://www.scribl.co/end-user-license-agreement'
            }}
            checked={acknowledgedConfirmation}
            onChange={(e) => setAcknowledgedConfirmation(e.target.checked)}
          />
        )}
      </form>
    </Container>
  );
}
